import React, { useState, useEffect, useCallback } from "react";
import { Form, Input, Select, Button, Alert, Card, Spin, Table, Tag, Tooltip, Modal } from "antd";
import {
  BellOutlined, TeamOutlined, GlobalOutlined, BankOutlined,
  ReloadOutlined, EyeOutlined, CheckCircleOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import api from "../../../core/axios/axiosInstance";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const { TextArea } = Input;

const TARGET_OPTIONS = [
  {
    value: "all",
    label: "Send to Everyone",
    description: "All company admins and all agents across every company",
    icon: <GlobalOutlined style={{ fontSize: 20, color: "#6366f1" }} />,
    color: "#ede9fe",
    border: "#6366f1",
  },
  {
    value: "companies",
    label: "Send to Company Admins",
    description: "Only company admin accounts (not agents)",
    icon: <BankOutlined style={{ fontSize: 20, color: "#0ea5e9" }} />,
    color: "#e0f2fe",
    border: "#0ea5e9",
  },
  {
    value: "specific",
    label: "Send to Specific Companies",
    description: "Select one or more companies — only their admins receive this",
    icon: <TeamOutlined style={{ fontSize: 20, color: "#f59e0b" }} />,
    color: "#fef3c7",
    border: "#f59e0b",
  },
];

const TARGET_LABELS = { all: "Everyone", companies: "Company Admins", specific: "Specific Companies" };
const TARGET_COLORS = { all: "purple", companies: "blue", specific: "orange" };

// ─── Simple bar chart (pure SVG, no extra dependency) ────────────────────────
function ReadUnreadChart({ readCount, unreadCount }) {
  const total = readCount + unreadCount || 1;
  const readPct = Math.round((readCount / total) * 100);
  const unreadPct = 100 - readPct;
  const barH = 28;
  const w = 300;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
      {/* Donut */}
      <svg width={140} height={140} viewBox="0 0 140 140">
        {(() => {
          const cx = 70, cy = 70, r = 52, stroke = 20;
          const circ = 2 * Math.PI * r;
          const readDash = (readCount / total) * circ;
          const unreadDash = circ - readDash;
          return (
            <>
              {/* background ring */}
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth={stroke} />
              {/* unread (grey) first */}
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke}
                strokeDasharray={`${unreadDash} ${circ}`}
                strokeDashoffset={0}
                transform={`rotate(-90 ${cx} ${cy})`} />
              {/* read (indigo) */}
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#6366f1" strokeWidth={stroke}
                strokeDasharray={`${readDash} ${circ}`}
                strokeDashoffset={0}
                transform={`rotate(-90 ${cx} ${cy})`} />
              <text x={cx} y={cy - 6} textAnchor="middle" fontSize={22} fontWeight={700} fill="#111827">{readPct}%</text>
              <text x={cx} y={cy + 14} textAnchor="middle" fontSize={11} fill="#9ca3af">opened</text>
            </>
          );
        })()}
      </svg>

      {/* Horizontal bars */}
      <svg width={w} height={80} viewBox={`0 0 ${w} 80`} style={{ overflow: "visible" }}>
        {/* Read bar */}
        <text x={0} y={14} fontSize={12} fill="#374151" fontWeight={600}>Read</text>
        <rect x={0} y={20} width={w} height={barH} rx={6} fill="#f3f4f6" />
        <rect x={0} y={20} width={(readCount / total) * w} height={barH} rx={6} fill="#6366f1" />
        <text x={(readCount / total) * w + 6} y={20 + barH / 2 + 4} fontSize={12} fill="#6366f1" fontWeight={700}>{readCount}</text>

        {/* Unread bar */}
        <text x={0} y={66} fontSize={12} fill="#374151" fontWeight={600}>Unread</text>
        <rect x={0} y={72} width={w} height={barH} rx={6} fill="#f3f4f6" />
        <rect x={0} y={72} width={(unreadCount / total) * w} height={barH} rx={6} fill="#f59e0b" />
        <text x={(unreadCount / total) * w + 6} y={72 + barH / 2 + 4} fontSize={12} fill="#f59e0b" fontWeight={700}>{unreadCount}</text>
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: "#6366f1", display: "inline-block" }} />
          Read ({readCount})
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: "#f59e0b", display: "inline-block" }} />
          Unread ({unreadCount})
        </div>
      </div>
    </div>
  );
}

// ─── Stats Modal ──────────────────────────────────────────────────────────────
function StatsModal({ log, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recipientFilter, setRecipientFilter] = useState("all"); // all | read | unread

  useEffect(() => {
    if (!log) return;
    setLoading(true);
    api
      .get(`/notifications/sent/${log._id}/stats`)
      .then((res) => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [log]);

  const filteredRecipients = (stats?.recipients || []).filter((r) => {
    if (recipientFilter === "read") return r.isRead;
    if (recipientFilter === "unread") return !r.isRead;
    return true;
  });

  const recipientCols = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{name || "—"}</div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (v) => <span style={{ fontSize: 13, color: "#6b7280" }}>{v || "—"}</span>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (r) => <Tag color={r === "companyAdmin" ? "blue" : "default"} style={{ fontSize: 11 }}>{r === "companyAdmin" ? "Admin" : "Agent"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "isRead",
      key: "isRead",
      width: 100,
      align: "center",
      render: (read) => read
        ? <CheckCircleOutlined style={{ color: "#6366f1", fontSize: 16 }} />
        : <ClockCircleOutlined style={{ color: "#f59e0b", fontSize: 16 }} />,
    },
  ];

  return (
    <Modal
      open={!!log}
      onCancel={onClose}
      footer={null}
      width="90vw"
      title={null}
      centered
      styles={{
        body: { padding: 0, maxHeight: "85vh", overflowY: "auto" },
        content: { padding: 0 },
      }}
    >
      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}><Spin /></div>
      ) : !stats ? (
        <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Failed to load stats.</div>
      ) : (
        <div>
          {/* Modal header */}
          <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 4 }}>{stats.log.title}</div>
            <Tag color={TARGET_COLORS[stats.log.target] || "default"} style={{ fontSize: 11 }}>
              {TARGET_LABELS[stats.log.target] || stats.log.target}
            </Tag>
            <span style={{ color: "#9ca3af", fontSize: 12, marginLeft: 8 }}>
              {dayjs(stats.log.createdAt).format("MMM D, YYYY h:mm A")}
            </span>
            {stats.log.target === "specific" && stats.log.targetCompanyNames?.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                Companies: {stats.log.targetCompanyNames.join(", ")}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 0, flexWrap: "wrap", minHeight: 500 }}>
            {/* Left — notification content */}
            <div style={{ flex: "1 1 340px", padding: "20px 24px", borderRight: "1px solid #f0f0f0", overflowY: "auto" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Short Description</div>
              <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, marginBottom: 16 }}>
                {stats.log.description || <span style={{ color: "#d1d5db" }}>None</span>}
              </p>

              {stats.log.body && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Full Message</div>
                  <div
                    style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, background: "#fafafa", borderRadius: 8, padding: "10px 12px" }}
                    dangerouslySetInnerHTML={{ __html: stats.log.body }}
                  />
                </>
              )}

              {/* Chart */}
              <div style={{ marginTop: 24, padding: "16px 0", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "center" }}>
                <ReadUnreadChart readCount={stats.readCount} unreadCount={stats.unreadCount} />
              </div>
            </div>

            {/* Right — recipient list */}
            <div style={{ flex: "1 1 360px", padding: "20px 24px", overflowY: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Recipients ({stats.recipients.length})
                </div>
                <Select
                  value={recipientFilter}
                  onChange={setRecipientFilter}
                  size="small"
                  style={{ width: 110 }}
                  options={[
                    { value: "all", label: "All" },
                    { value: "read", label: "Read" },
                    { value: "unread", label: "Unread" },
                  ]}
                />
              </div>
              <Table
                columns={recipientCols}
                dataSource={filteredRecipients}
                rowKey="_id"
                size="small"
                pagination={{ pageSize: 6, size: "small", showSizeChanger: false }}
                scroll={{ y: 280 }}
                locale={{ emptyText: "No recipients" }}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminNotifications() {
  const [form] = Form.useForm();
  const [target, setTarget] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Sent history table
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // Stats modal
  const [activeLog, setActiveLog] = useState(null);

  // ── AI generation ─────────────────────────────────────────────────────────
  const [aiPrompt, setAiPrompt]           = useState("");
  const [aiGenerating, setAiGenerating]   = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiError, setAiError]             = useState(null);
  const [selectedAi, setSelectedAi]       = useState(null);

  const handleGenerateNotif = async () => {
    if (!aiPrompt.trim()) return;
    setAiGenerating(true);
    setAiSuggestions([]);
    setAiError(null);
    setSelectedAi(null);
    try {
      const res = await api.post("/ai/generate-content", { prompt: aiPrompt, type: "notification" });
      setAiSuggestions(res.data?.suggestions || []);
    } catch (err) {
      setAiError(err.response?.data?.message || "AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSelectNotifSuggestion = (s) => {
    setSelectedAi(s);
    form.setFieldsValue({
      title:       s.title       || "",
      description: s.description || "",
      body:        s.body        || "",
    });
  };

  // Load companies when "specific" is selected
  useEffect(() => {
    if (target !== "specific" || companies.length > 0) return;
    setCompaniesLoading(true);
    api
      .post("/superAdmin/allCompanyAdmin", { limit: 1000 })
      .then((res) => {
        const list = res.data?.data || [];
        setCompanies(Array.isArray(list) ? list : []);
      })
      .catch(() => setCompanies([]))
      .finally(() => setCompaniesLoading(false));
  }, [target]);

  const fetchLogs = useCallback((page = 1, pageSize = 10) => {
    setLogsLoading(true);
    api
      .get("/notifications/sent", { params: { page, limit: pageSize } })
      .then((res) => {
        setLogs(res.data?.logs || []);
        setLogsPagination({ current: page, pageSize, total: res.data?.total || 0 });
      })
      .catch(() => setLogs([]))
      .finally(() => setLogsLoading(false));
  }, []);

  useEffect(() => { fetchLogs(1, 10); }, [fetchLogs]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setResult(null);
    try {
      const payload = {
        title: values.title,
        description: values.description || "",
        body: values.body || "",
        target,
        ...(target === "specific" && { targetCompanyIds: values.targetCompanyIds || [] }),
      };
      const res = await api.post("/notifications", payload);
      setResult({ type: "success", message: res.data?.message || "Notification sent successfully!" });
      form.resetFields(["title", "description", "body", "targetCompanyIds"]);
      fetchLogs(1, logsPagination.pageSize);
    } catch (err) {
      setResult({ type: "error", message: err.response?.data?.message || "Failed to send notification." });
    } finally {
      setSubmitting(false);
    }
  };

  const companyOptions = companies.map((c) => {
    const companyName = c.userInfo?.companyName;
    const ownerName = [c.firstname, c.lastname].filter(Boolean).join(" ");
    const label = companyName
      ? `${companyName}${ownerName ? ` (${ownerName})` : ""}`
      : ownerName || c.email || c._id;
    return { value: c._id, label };
  });

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 180,
      render: (text) => <span style={{ fontWeight: 600, color: "#111827" }}>{text}</span>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (text) => (
        <span style={{ color: "#6b7280", fontSize: 13 }}>
          {text ? (text.length > 60 ? text.slice(0, 60) + "…" : text) : "—"}
        </span>
      ),
    },
    {
      title: "Target",
      dataIndex: "target",
      key: "target",
      width: 160,
      render: (t, record) => (
        <div>
          <Tag color={TARGET_COLORS[t] || "default"}>{TARGET_LABELS[t] || t}</Tag>
          {t === "specific" && record.targetCompanyNames?.length > 0 && (
            <Tooltip title={record.targetCompanyNames.join(", ")}>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2, cursor: "default" }}>
                {record.targetCompanyNames.length === 1
                  ? record.targetCompanyNames[0]
                  : `${record.targetCompanyNames[0]} +${record.targetCompanyNames.length - 1} more`}
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Recipients",
      dataIndex: "recipientCount",
      key: "recipientCount",
      width: 100,
      align: "center",
      render: (n) => (
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "#f3f4f6", borderRadius: 20, padding: "2px 10px",
          fontWeight: 700, fontSize: 13, color: "#374151",
        }}>{n}</span>
      ),
    },
    {
      title: "Sent At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (d) => (
        <Tooltip title={dayjs(d).format("MMM D, YYYY h:mm A")}>
          <span style={{ color: "#6b7280", fontSize: 13 }}>{dayjs(d).fromNow()}</span>
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setActiveLog(record)}
          style={{ borderColor: "#6366f1", color: "#6366f1" }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content" style={{ margin: "0 auto" }}>
        {/* Page header */}
        <div className="d-flex align-items-center gap-2 mb-4">
          <BellOutlined style={{ fontSize: 24, color: "#6366f1" }} />
          <div>
            <h4 className="mb-0" style={{ fontWeight: 700 }}>Direct Notification</h4>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>Send in-app notifications to your users</p>
          </div>
        </div>

        {/* Audience selector */}
        <div className="mb-4">
          <div className="fw-semibold mb-2" style={{ fontSize: 14, color: "#374151" }}>Select Audience</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {TARGET_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                onClick={() => { setTarget(opt.value); setResult(null); form.resetFields(["targetCompanyIds"]); }}
                style={{
                  flex: "1 1 200px",
                  border: `2px solid ${target === opt.value ? opt.border : "#e5e7eb"}`,
                  borderRadius: 12,
                  padding: "14px 16px",
                  cursor: "pointer",
                  background: target === opt.value ? opt.color : "#fff",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <div style={{ marginTop: 2 }}>{opt.icon}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>{opt.label}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>{opt.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <Card style={{ borderRadius: 12 }} styles={{ body: { padding: 24 } }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>

            {target === "specific" && (
              <Form.Item
                label="Select Companies"
                name="targetCompanyIds"
                rules={[{ required: true, message: "Please select at least one company" }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder={companiesLoading ? "Loading companies…" : "Search and select companies"}
                  loading={companiesLoading}
                  notFoundContent={companiesLoading ? <Spin size="small" /> : "No companies found"}
                  filterOption={(input, opt) =>
                    (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  maxTagCount="responsive"
                  options={companyOptions}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            )}

            <Form.Item
              label="Notification Title"
              name="title"
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input placeholder="e.g. System Maintenance Scheduled" maxLength={120} showCount />
            </Form.Item>

            <Form.Item
              label="Short Description"
              name="description"
              extra="Shown in the notification list preview (keep it brief)"
            >
              <Input placeholder="Brief summary shown in the notification list…" maxLength={240} showCount />
            </Form.Item>

            <Form.Item
              label="Full Message Body"
              name="body"
              extra="Supports HTML. Shown when the user opens the notification."
            >
              <TextArea
                rows={5}
                placeholder="Write the full notification content here. You can use HTML tags like <b>, <a>, <ul>, <p>…"
              />
            </Form.Item>

            {result && (
              <Alert
                type={result.type}
                message={result.message}
                showIcon
                closable
                onClose={() => setResult(null)}
                style={{ marginBottom: 16 }}
              />
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<BellOutlined />}
                size="large"
                style={{ background: "#6366f1", borderColor: "#6366f1", minWidth: 160 }}
              >
                {submitting ? "Sending…" : "Send Notification"}
              </Button>
            </div>
          </Form>
        </Card>

        {/* Sent history table */}
        <div style={{ marginTop: 32 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Sent Notifications</div>
              <div style={{ color: "#9ca3af", fontSize: 12 }}>History of all notifications you have sent</div>
            </div>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchLogs(logsPagination.current, logsPagination.pageSize)}
              loading={logsLoading}
              size="small"
            >
              Refresh
            </Button>
          </div>
          <Card style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
            <Table
              columns={columns}
              dataSource={logs}
              rowKey="_id"
              loading={logsLoading}
              pagination={{
                current: logsPagination.current,
                pageSize: logsPagination.pageSize,
                total: logsPagination.total,
                showSizeChanger: true,
                showTotal: (total) => `${total} sent`,
                onChange: (page, pageSize) => fetchLogs(page, pageSize),
              }}
              locale={{ emptyText: "No notifications sent yet" }}
              scroll={{ x: 700 }}
              size="middle"
            />
          </Card>
        </div>
      </div>

      {/* Stats modal */}
      <StatsModal log={activeLog} onClose={() => setActiveLog(null)} />
    </div>
  );
}
